import React, { useEffect, useLayoutEffect, useRef } from "react";
import PropTypes from "prop-types";
import Avatar from "./Avatar";
import ChannelBadge from "./ChannelBadge";
import MessageBubble from "./MessageBubble";
import AssigneeSelect from "./AssigneeSelect";
import TagChips from "./TagChips";
import NotesPanel from "./NotesPanel";
import { IdentityBadge } from "../../components/ui";
import {
  getHistoryMessages,
  getDisplayName,
  getChannelConfig,
  getSessionIdentity,
  getSessionTags,
  getQueueStatus,
  classifyRole,
  compactRelativeTime,
} from "./messageCenterHelpers";
import styles from "./ConversationThread.module.css";

/** Estado inicial — nenhuma conversa selecionada. */
function EmptyState() {
  return (
    <div className={styles.emptyState}>
      <span className={styles.emptyIcon} aria-hidden="true">
        💬
      </span>
      <h2 className={styles.emptyTitle}>Selecione uma conversa</h2>
      <p className={styles.emptyText}>
        Escolha uma conversa à esquerda para ver o histórico e responder ao
        cliente.
      </p>
    </div>
  );
}

/** Skeleton de bolhas enquanto a thread carrega. */
function ThreadSkeleton() {
  const layout = ["in", "out", "in", "out"];
  return (
    <div className={styles.feed} aria-hidden="true">
      {layout.map((side, i) => (
        <span
          key={i}
          className={`${styles.skeletonBubble} ${
            side === "out" ? styles.skeletonOut : styles.skeletonIn
          }`}
        />
      ))}
    </div>
  );
}

/**
 * Painel direito: header da conversa com botão de controle, feed cronológico de
 * mensagens (auto-scroll para a mais recente) e o composer de resposta.
 */
export default function ConversationThread({
  session,
  loadingThread,
  onSend,
  onAssumeControl,
  onReleaseControl,
  controlBusy,
  onFinish,
  onReopen,
  queueBusy,
  onAssign,
  assignBusy,
  onSetTags,
  tagsBusy,
  onNotesError,
  onFeatureBlocked,
  currentMemberId,
  sending,
  draft,
  onDraftChange,
  onBack,
  crmEnabled = true,
}) {
  const feedRef = useRef(null);
  const textareaRef = useRef(null);

  const messages = session ? getHistoryMessages(session.history) : [];
  const humanControl = session?.humanControlActive ?? false;

  // Auto-scroll para a mensagem mais recente ao abrir a thread e a cada update.
  useLayoutEffect(() => {
    const el = feedRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [session?.id, messages.length, loadingThread]);

  // Textarea cresce até 4 linhas.
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    const maxHeight = 4 * 22 + 20; // ~4 linhas + padding
    el.style.height = `${Math.min(el.scrollHeight, maxHeight)}px`;
  }, [draft]);

  if (!session) {
    return (
      <section className={styles.panel}>
        <EmptyState />
      </section>
    );
  }

  const displayName = getDisplayName(session.clientName);
  const channel = getChannelConfig(session.platform);
  const identity = getSessionIdentity(session);
  const isDone = getQueueStatus(session) === "DONE";
  const isAssignedToMe =
    Boolean(currentMemberId) && session.assignedToId === currentMemberId;
  const lastMsgTime = compactRelativeTime(
    humanControl ? session.humanControlAt : session.updatedAt,
  );

  const handleKeyDown = (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      onSend();
    }
  };

  const composerDisabled = !humanControl || sending;

  return (
    <section className={styles.panel}>
      <header className={styles.header}>
        {onBack && (
          <button
            type="button"
            className={styles.backBtn}
            onClick={onBack}
            aria-label="Voltar para a lista de conversas"
          >
            ←
          </button>
        )}
        <Avatar name={displayName} size="lg" humanControl={humanControl} />
        <div className={styles.headerInfo}>
          <span className={styles.headerName}>{displayName}</span>
          <span className={styles.headerMeta}>
            <ChannelBadge platform={session.platform} compact />
            <span className={styles.headerChannelLabel}>{channel.label}</span>
            {lastMsgTime && <span> · Última mensagem {lastMsgTime}</span>}
          </span>
          <span className={styles.headerIdentity}>
            <IdentityBadge
              state={identity.state}
              username={identity.username}
              phone={identity.phoneNumber}
              size="sm"
            />
          </span>
        </div>

        <div className={styles.headerActions}>
          {/* Finalizar/Reabrir move a conversa entre filas — recurso de equipe
              (CRM), escondido no Básico. Assumir/devolver controle NÃO é gateado. */}
          {crmEnabled &&
            (isDone ? (
              <button
                type="button"
                className={styles.queueBtn}
                onClick={onReopen}
                disabled={queueBusy}
                aria-busy={queueBusy}
                aria-label="Reabrir a conversa (mover para a entrada)"
              >
                {queueBusy ? "Reabrindo…" : "↩ Reabrir"}
              </button>
            ) : (
              <button
                type="button"
                className={`${styles.queueBtn} ${styles.finishBtn}`}
                onClick={onFinish}
                disabled={queueBusy}
                aria-busy={queueBusy}
                aria-label="Finalizar a conversa (mover para finalizados)"
              >
                {queueBusy ? "Finalizando…" : "✓ Finalizar"}
              </button>
            ))}

          {humanControl ? (
            <button
              type="button"
              className={`${styles.controlBtn} ${styles.controlActive}`}
              onClick={onReleaseControl}
              disabled={controlBusy}
              aria-busy={controlBusy}
              aria-label="Devolver o controle da conversa para a IA"
            >
              <span className={styles.pulse} aria-hidden="true" />
              {controlBusy ? "Devolvendo…" : "🤖 Devolver para IA"}
            </button>
          ) : (
            <button
              type="button"
              className={styles.controlBtn}
              onClick={onAssumeControl}
              disabled={controlBusy}
              aria-busy={controlBusy}
              aria-label="Assumir o controle da conversa"
            >
              {controlBusy ? "Assumindo…" : "👤 Assumir controle"}
            </button>
          )}
        </div>
      </header>

      {/* Barra de atribuição (setor/atendente, "Pegar conversa", desatribuir) é
          recurso de equipe (CRM) — só o Profissional a vê. */}
      {crmEnabled && !isDone && (
        <div className={styles.assignBar}>
          <AssigneeSelect
            session={session}
            onAssign={onAssign}
            busy={assignBusy}
          />
          {isAssignedToMe ? (
            <div className={styles.selfActions}>
              <span
                className={styles.assignedSelf}
                title="Você é o atendente desta conversa"
              >
                ✓ Você está atendendo
              </span>
              <button
                type="button"
                className={styles.unassignBtn}
                onClick={() => onAssign({ assignedToId: null })}
                disabled={assignBusy}
                aria-busy={assignBusy}
                aria-label="Desatribuir esta conversa de você"
              >
                {assignBusy ? "Desatribuindo…" : "Desatribuir"}
              </button>
            </div>
          ) : (
            <button
              type="button"
              className={styles.grabBtn}
              onClick={() => onAssign({ assignedToId: currentMemberId })}
              disabled={assignBusy || !currentMemberId}
              aria-busy={assignBusy}
              aria-label="Pegar esta conversa para você"
            >
              {assignBusy ? "Pegando…" : "🙋 Pegar conversa"}
            </button>
          )}
        </div>
      )}

      {/* Painel de detalhes da conversa — tags (F2.5) e notas internas (F2.6).
          Ambos são recurso de equipe (CRM): ocultos no Básico. */}
      {crmEnabled && (
        <div className={styles.detailsPanel}>
          <TagChips
            tags={getSessionTags(session)}
            onChange={onSetTags}
            busy={tagsBusy}
          />
          <NotesPanel
            sessionId={session.id}
            onError={onNotesError}
            onFeatureBlocked={onFeatureBlocked}
          />
        </div>
      )}

      {loadingThread ? (
        <ThreadSkeleton />
      ) : (
        <div className={styles.feed} ref={feedRef} role="log" aria-live="polite">
          {messages.map((msg, idx) => (
            <MessageBubble
              key={idx}
              text={msg.text}
              kind={classifyRole(msg.role)}
              mediaType={msg.mediaType}
              pending={msg.__pending}
            />
          ))}
        </div>
      )}

      <div className={styles.composer}>
        {!humanControl && (
          <p className={styles.composerHint}>
            🤖 A IA está respondendo. Clique em “Assumir controle” para responder
            manualmente.
          </p>
        )}
        <div className={styles.composerRow}>
          <textarea
            ref={textareaRef}
            className={styles.textarea}
            rows={1}
            value={draft}
            onChange={(e) => onDraftChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={
              humanControl
                ? "Digite sua mensagem... (Ctrl+Enter para enviar)"
                : "Assuma o controle para responder"
            }
            disabled={composerDisabled}
            aria-label="Mensagem de resposta"
          />
          <button
            type="button"
            className={styles.sendBtn}
            onClick={onSend}
            disabled={composerDisabled || !draft.trim()}
            aria-label="Enviar mensagem"
          >
            {sending ? "…" : "Enviar ↵"}
          </button>
        </div>
      </div>
    </section>
  );
}

ConversationThread.propTypes = {
  session: PropTypes.object,
  loadingThread: PropTypes.bool,
  onSend: PropTypes.func.isRequired,
  onAssumeControl: PropTypes.func.isRequired,
  onReleaseControl: PropTypes.func.isRequired,
  controlBusy: PropTypes.bool,
  onFinish: PropTypes.func.isRequired,
  onReopen: PropTypes.func.isRequired,
  queueBusy: PropTypes.bool,
  onAssign: PropTypes.func.isRequired,
  assignBusy: PropTypes.bool,
  onSetTags: PropTypes.func.isRequired,
  tagsBusy: PropTypes.bool,
  onNotesError: PropTypes.func,
  onFeatureBlocked: PropTypes.func,
  currentMemberId: PropTypes.string,
  sending: PropTypes.bool,
  draft: PropTypes.string.isRequired,
  onDraftChange: PropTypes.func.isRequired,
  onBack: PropTypes.func,
  crmEnabled: PropTypes.bool,
};
